<template>
  <div class="mt-3">
    <McContactTable
      :apiClient="query"
      :fields="fields"
      :hasData="hasData"
      :disabled="isAdding"
      @updateCountryCode="updateCountryCode"
      :listDatas="employeesList"
      @customTag="addTag"
      :withCard="false"
      :cardPadding="false"
      :emptyState="$t('dashboard.scheduler.emptyState.title')"
      :emptyStateDesc="$t('dashboard.scheduler.emptyState.desc')"
    >
    </McContactTable>
  </div>
</template>

<script>
import {
  GET_CONTACTS,
  GET_CONTACT,
  CREATE_CONTACT,
  UPDATE_CONTACT,
  DELETE_CONTACT,
} from '@/services/api/notification/contact'
import { GET_EMPLOYEES_PAGINATED } from '@/services/api/master/employees'
import McContactTable from '@/components/McContactTable'
import { mapGetters } from 'vuex'

export default {
  components: {
    McContactTable,
  },

  props: ['isAdding'],

  data() {
    return {
      countryCode: '+62',
      hasData: true,
      employeesList: [],
      render: false,
    }
  },

  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    fields() {
      return [
        {
          name: 'user',
          key: 'user.fullname',
          label: this.$t('notification.setting.emailNotification.recipientName'),
          placeholder: this.$t('notification.setting.emailNotification.recipientName'),
          isEditable: true,
          type: 'select-resourceful-v2',
          optionsGetter: this.resourcefulQuery.employees,
          trackBy: 'userId',
          optionLabelFn: ({ item }) => item.fullname ?? item.label,
          multiple: false,
          taggable: true,
          thStyle: 'width: 400px',
          customColumn: true,
          validationBehavior: 'blur',
          errorBehavior: 'blur',
        },
        {
          name: 'phoneNumber',
          key: 'phoneNumber',
          label: this.$t('notification.setting.emailNotification.whatsappNo'),
          placeholder: this.$t('notification.setting.emailNotification.whatsappNo'),
          type: 'phone-number',
          isEditable: true,
          validation: [['isPhoneNumber', this.countryCode]],
          customColumn: true,
          validationBehavior: 'submit',
          errorBehavior: 'submit',
          disabled: this.hasWhatsapp,
        },
        {
          name: 'email',
          key: 'email',
          label: this.$t('notification.setting.emailNotification.email'),
          placeholder: this.$t('notification.setting.emailNotification.email'),
          isEditable: true,
          type: 'text',
          thStyle: 'width: 400px',
          validation: 'optional|email',
          validationBehavior: 'blur',
          errorBehavior: 'blur',
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          if (this.isAdding) {
            return {
              data: [],
              pagination: {
                page: 1,
                total: 0,
                show: 10,
              },
            }
          } else {
            const res = await this.$apollo.query({
              query: GET_CONTACTS,
              fetchPolicy: 'no-cache',
              variables: {
                filter: {
                  ...filter,
                  emailNotificationId: [Number(this.$route.params.id)],
                },
              },
            })

            const self = this
            if (res.data.contacts.contacts) self.hasData = true
            else self.hasData = false

            return res.data.contacts.contacts
          }
        },
        getById: async (id) => {
          const res = await this.$apollo.query({
            query: GET_CONTACT,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                contactId: +id,
              },
            },
          })

          return res.data.contact
        },

        store: async (item, obj) => {
          let user = {}

          if (item.user) {
            if (item.user == 9999999) {
              user = {
                label: obj.label,
                userId: null,
              }
            } else {
              user = {
                userId: obj.userId,
                label: null,
              }
            }
          } else {
            user = { userId: null, label: null }
          }

          let input = {
            ...user,
            emailNotificationId: Number(this.$route.params.id),
            phoneNumber: item.phoneNumber ? this.countryCode.slice(1) + item.phoneNumber : null,
            email: item.email ?? null,
          }

          const res = await this.$apollo.mutate({
            mutation: CREATE_CONTACT,
            variables: {
              input: input,
            },
          })

          return res.data.createContact
        },

        update: async (contactId, item, obj) => {
          let user = {}

          if (item.user) {
            if (item.user == 9999999) {
              user = {
                label: obj.label,
                userId: null,
              }
            } else {
              user = {
                userId: obj.userId,
                label: null,
              }
            }
          } else {
            user = { userId: null, label: null }
          }

          let input = {
            ...user,
            phoneNumber: item.phoneNumber ? this.countryCode.slice(1) + item.phoneNumber : null,
            email: item.email ?? null,
            contactId: +contactId,
            emailNotificationId: Number(this.$route.params.id),
          }

          const res = await this.$apollo.mutate({
            mutation: UPDATE_CONTACT,
            variables: {
              input: input,
            },
          })

          return res.data.updateContact
        },
        delete: async (contactId) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_CONTACT,
            variables: {
              input: {
                contactId: +contactId,
              },
            },
          })

          return res.data.deleteContact
        },
      }
    },

    resourcefulQuery() {
      return {
        employees: async (opts) => {
          let result = {}
          let employeeList = this.employeesList

          if (opts?.page === 1) employeeList = []

          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_EMPLOYEES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          const employees = res.data.employeesPaginated.employeesPaginated

          const self = this
          employees.data.map((item) => employeeList.push(item))

          self.employeesList = employeeList

          result = {
            metadata: {
              total_count: employees.pagination.total,
              page: employees.pagination.page,
            },
            data: {
              paginated_result: employees.data,
            },
          }

          return result
        },
      }
    },

    hasWhatsapp() {
      const disabled = this.hasAccessAll(['whatsapp-integration:read'])
      return !disabled
    },
  },

  watch: {
    countryCode: {
      handler(value) {
        if (value) {
          if (value.includes('+')) this.countryCode = value
          else this.countryCode = `+${value}`
        } else this.countryCode = '+62'
      },
    },
  },

  methods: {
    updateCountryCode(value) {
      this.countryCode = value
    },

    addTag(obj) {
      this.employeesList.push(obj)
    },
  },
}
</script>
